import React, { useCallback, useEffect, useState } from 'react';
import Spinner from '../../../components/ui/Spinner';
import { useAuth } from '../../../contexts/AuthContext';
import { clinicalDelegationService, type MyAssistant } from '../../../services/clinicalDelegationService';

/**
 * The doctor's own assistants and their delegated-signing consent
 * (clinical-delegation R7, R8; design: "toggle with confirmation text
 * stating the doctor's responsibility"). Enabling is a deliberate act: a
 * dialog explains the responsibility and needs an explicit acknowledgement.
 * The consent copy is pending legal review (Ley 126-02).
 *
 * The spinner shows on the first load only, so reloading the list after a
 * change never unmounts the open dialog or its error (PR 4c lesson).
 */
const MyAssistantsCard: React.FC = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<MyAssistant[] | null>(null);
  const [pending, setPending] = useState<MyAssistant | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => clinicalDelegationService.getMyAssistants()
    .then(setItems)
    .catch(() => setItems((current) => current ?? [])), []);

  useEffect(() => { load(); }, [load]);

  const open = (assistant: MyAssistant) => {
    setPending(assistant);
    setAcknowledged(false);
    setError(null);
  };

  const confirm = async () => {
    if (!pending) return;
    setSaving(true);
    setError(null);
    try {
      await clinicalDelegationService.setAssistantSigning(pending.id, !pending.can_sign);
      setPending(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo cambiar la autorización de firma.');
    } finally {
      setSaving(false);
      load();
    }
  };

  const doctorName = `Dr. ${user?.doctor?.first_name ?? ''} ${user?.doctor?.last_name ?? ''}`.trim();
  const enabling = !!pending && !pending.can_sign;
  const name = pending?.assistant_name || 'este asistente';
  const title = enabling ? 'Autorizar firma delegada' : 'Retirar firma delegada';

  return (
    <div className="card">
      <div className="card-header">
        <h4 className="card-title d-inline-block">Mis Asistentes</h4>
      </div>
      <div className="card-body p-0 table-dash">
        <div className="table-responsive">
          <table className="table mb-0 border-0 custom-table">
            <thead>
              <tr>
                <th>Asistente</th>
                <th>Firma delegada</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items === null ? (
                <tr><td colSpan={3} className="text-center py-3"><Spinner /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={3} className="text-center text-muted py-3">No tiene asistentes asignados</td></tr>
              ) : (
                items.map((a) => (
                  <tr key={a.id}>
                    <td>{a.assistant_name || `#${a.assistant_user_id}`}</td>
                    <td>
                      <span className={`custom-badge ${a.can_sign ? 'status-green' : 'status-grey'}`}>
                        {a.can_sign ? 'Autorizada' : 'No autorizada'}
                      </span>
                      {a.can_sign && !a.can_sign_effective && (
                        <div className="text-muted small mt-1">Sin efecto por ahora: la firma delegada no está habilitada en la clínica.</div>
                      )}
                    </td>
                    <td className="text-end">
                      <button className={`btn btn-sm ${a.can_sign ? 'btn-outline-danger' : 'btn-outline-primary'}`} onClick={() => open(a)}>
                        {a.can_sign ? 'Retirar firma' : 'Autorizar firma'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pending && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} role="dialog" aria-modal="true" aria-labelledby="signing-consent-title">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="signing-consent-title">{title}</h5>
                <button type="button" className="btn-close" onClick={() => setPending(null)} aria-label="Cerrar" disabled={saving}></button>
              </div>
              <div className="modal-body">
                {enabling ? (
                  <>
                    <p>
                      Va a autorizar a <strong>{name}</strong> a firmar en su nombre las consultas que registre para usted.
                      Usted sigue siendo el médico responsable, clínica y legalmente, de todo lo que se firme en su nombre.
                    </p>
                    <p>
                      Cada registro firmado así lo indicará en pantalla y en los documentos impresos:
                      “Firmado por delegación: {name} en nombre de {doctorName}”.
                      Puede retirar esta autorización en cualquier momento; lo ya firmado no cambia.
                    </p>
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="signing-consent-ack" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />
                      <label className="form-check-label" htmlFor="signing-consent-ack">
                        Entiendo que respondo por lo que {name} firme en mi nombre.
                      </label>
                    </div>
                  </>
                ) : (
                  <p>
                    {name} dejará de poder firmar en su nombre de inmediato. Sus borradores pendientes
                    tendrá que firmarlos usted. Los registros ya firmados no cambian.
                  </p>
                )}
                {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setPending(null)} disabled={saving}>Cancelar</button>
                <button
                  type="button"
                  className={`btn ${enabling ? 'btn-primary' : 'btn-danger'}`}
                  disabled={saving || (enabling && !acknowledged)}
                  onClick={confirm}
                >
                  {enabling ? 'Autorizar firma' : 'Retirar firma'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyAssistantsCard;
